import { Box, Typography } from "@material-ui/core";
import Highcharts from "highcharts";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import LineAndScatterChart from "src/components/ScoringChart/LineAndScatterChart";
import { beautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import { FigureTypography } from "src/components/Typography/TitleTypography";
import TitleTypography from "src/components/Typography/TitleTypography";
import OverviewMonitoringWrapper from "src/components/Wrapper/OverviewMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import { useUserContext } from "src/context/config/userContext";
import {
  useUserMonitoringChartSelector,
  useUserMonitoringSelector,
} from "src/context/monitoring/userMonitoringContext";
import { numberWithCommas, toFixed } from "src/services/utils";

const getOptions = () => ({
  chart: {
    height: 260,
  },
  xAxis: {
    labels: {
      enabled: false,
    },
  },
  yAxis: [
    {
      labels: {
        enabled: false,
      },
      title: {
        text: "",
      },
      gridLineWidth: 0,
    },
    {
      labels: {
        enabled: false,
      },
      opposite: true,
      title: {
        text: "",
      },
      gridLineWidth: 0,
    },
  ],
  ...beautifulTooltip((value) => numberWithCommas(toFixed(value, 4))),
  plotOptions: {
    scatter: {
      tooltip: {
        headerFormat: "<b>{series.name}</b><br>",
        pointFormat: "{point.y}",
      },
    },
  },
});

export default function LiquidationHistoryOverview() {
  const { pool } = useParams();
  const { role } = useUserContext();
  const { fetchLiquidationHistory } = useUserMonitoringSelector((state) => state.fetch);
  const liquidationHistory = useUserMonitoringChartSelector(MONITORING.USER.liquidationHistory, pool);

  useEffect(() => {
    var currentTime = Math.floor(Date.now() / 1000);
    fetchLiquidationHistory(
      pool,
      { from: currentTime - 604800, to: currentTime },
      { active: 1, isCheckMetadata: false }
    );
  }, [pool, fetchLiquidationHistory]);

  const { series, lastHealthFactor, date, check } = useMemo(() => {
    const check = liquidationHistory?.healthFactor?.length > 0;
    let series = [
      { type: "line", name: "Health Factor", data: [] },
      { type: "scatter", name: "Liquidation History", data: [], yAxis: 1 },
    ];
    let lastHealthFactor = 0;
    let date = "";
    if (check) {
      series = [
        {
          type: "line",
          name: "Health Factor",
          data: [].concat(liquidationHistory.healthFactor),
        },
        {
          type: "scatter",
          name: "Liquidation History",
          data: [].concat(liquidationHistory.liquidationChart),
          yAxis: 1,
        },
      ];
      const lastValue = liquidationHistory.healthFactor.slice(-1)[0];
      lastHealthFactor = lastValue[1];
      date = Highcharts.dateFormat("%b %e, %Y, %k:%M", lastValue[0]);
    }
    return { series, check, lastHealthFactor, date, isLoading: check };
  }, [liquidationHistory]);

  return (
    <Box mt={2}>
      <OverviewMonitoringWrapper
        title="Health Factor and Liquidation History"
        overview={
          role === "admin"
            ? "The chart shows the health factor and liquidation history of a user in a lending pool."
            : "The chart shows your health factor and liquidation history."
        }
        to={`/user-monitoring/health-factor-and-liquidation-history/${pool}`}
      >
        {check ? (
          <Box>
            <Box display="flex" flexWrap="wrap">
              <FigureTypography value={`${numberWithCommas(toFixed(lastHealthFactor, 5))}`} title="Health Factor" />
            </Box>
            <Typography>{date}</Typography>
            <LineAndScatterChart series={series} option={getOptions()} />
          </Box>
        ) : role === "admin" ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <TitleTypography textProps={{ variant: "h4" }} title="This account doesn't have any transactions" />
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <TitleTypography textProps={{ variant: "h4" }} title="You don't have any transactions" />
          </Box>
        )}
      </OverviewMonitoringWrapper>
    </Box>
  );
}
